import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { Field } from './Field';
import { Text } from './Text';
import { colors } from '@/theme/tokens';
import styles from './CredentialsSheet.module.css';

interface CredentialsSheetProps {
  open: boolean;
  /** The login being replaced, shown so there is no doubt whose account this is. */
  currentLogin?: string;
  busy?: boolean;
  /** Lao, from the server. Shown inside the sheet so the fields stay in view. */
  error?: string | null;
  onSubmit: (change: { currentPassword: string; login?: string; password?: string }) => void;
  onClose: () => void;
}

/** Mirrors `MIN_LOGIN_LENGTH` / `MIN_PASSWORD_LENGTH` in `accounts/api.py`. */
const MIN_LOGIN = 4;
const MIN_PASSWORD = 6;

/**
 * Changing your own login and password.
 *
 * Both in one sheet because they are one errand — a shopkeeper handed an
 * account by whoever set the shop up changes both at once — and because either
 * one alone needs the same current password and ends the same other sessions.
 * Leaving a field blank leaves that half alone.
 *
 * The new password is typed twice. There is no "forgot password" in this
 * product; a typo in a field nobody can read back locks the person out of their
 * own till until somebody with database access fixes it.
 */
export function CredentialsSheet({
  open,
  currentLogin,
  busy = false,
  error,
  onSubmit,
  onClose,
}: CredentialsSheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [current, setCurrent] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setCurrent('');
      setLogin('');
      setPassword('');
      setAgain('');
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (!open) return null;

  const nextLogin = login.trim();
  // The login the account will end up with, which is what the password may not
  // equal — leaving the login field alone does not make the old one fair game.
  const problem = check(nextLogin, nextLogin || currentLogin || '', password, again, current);
  const canSave = current.length > 0 && problem === null && (nextLogin !== '' || password !== '');

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className={styles.sheet}>
        <div className={styles.grabber} />

        <Text weight={800} size={16}>
          ປ່ຽນຊື່ຜູ້ໃຊ້ / ລະຫັດຜ່ານ
        </Text>
        {currentLogin ? (
          <Text size={11.5} color={colors.textMuted}>
            ບັນຊີ {currentLogin}
          </Text>
        ) : null}

        <div className={styles.field}>
          <Text weight={700} size={11} color={colors.textMuted}>
            ລະຫັດຜ່ານປັດຈຸບັນ
          </Text>
          <Field value={current} onChange={setCurrent} secret label="ລະຫັດຜ່ານປັດຈຸບັນ" autoFocus />
        </div>

        <div className={styles.field}>
          <Text weight={700} size={11} color={colors.textMuted}>
            ຊື່ຜູ້ໃຊ້ໃໝ່ · ຫວ່າງໄວ້ = ບໍ່ປ່ຽນ
          </Text>
          <Field value={login} onChange={setLogin} label="ຊື່ຜູ້ໃຊ້ໃໝ່" placeholder={currentLogin} />
        </div>

        <div className={styles.field}>
          <Text weight={700} size={11} color={colors.textMuted}>
            ລະຫັດຜ່ານໃໝ່ · ຫວ່າງໄວ້ = ບໍ່ປ່ຽນ
          </Text>
          <Field value={password} onChange={setPassword} secret label="ລະຫັດຜ່ານໃໝ່" />
        </div>

        {/* Always here, not revealed once the field above is typed in. Hidden,
            it reads as "this form does not confirm the password" — and a field
            that appears mid-form shifts everything under the thumb that is
            already moving. */}
        <div className={styles.field}>
          <Text weight={700} size={11} color={colors.textMuted}>
            ພິມລະຫັດຜ່ານໃໝ່ອີກເທື່ອ
          </Text>
          <Field value={again} onChange={setAgain} secret label="ຢືນຢັນລະຫັດຜ່ານໃໝ່" />
        </div>

        {error ?? problem ? (
          <Text size={11.5} weight={700} color={colors.skyDeep}>
            {error ?? problem}
          </Text>
        ) : null}

        {/* Said before they commit, not discovered afterwards: the other phone
            in the shop stops working and has to sign in again. */}
        <Text size={10.5} color={colors.textFaint}>
          ປ່ຽນແລ້ວ ເຄື່ອງອື່ນທີ່ໃຊ້ບັນຊີນີ້ຈະຕ້ອງເຂົ້າສູ່ລະບົບໃໝ່ · ເຄື່ອງນີ້ໃຊ້ຕໍ່ໄດ້ເລີຍ
        </Text>

        <div className={styles.actions}>
          <Button label="ກັບຄືນ" variant="outline" className="grow" onPress={onClose} />
          <Button
            label={busy ? 'ກຳລັງບັນທຶກ…' : 'ບັນທຶກ'}
            className="grow"
            disabled={!canSave || busy}
            onPress={() =>
              onSubmit({
                currentPassword: current,
                ...(nextLogin ? { login: nextLogin } : {}),
                ...(password ? { password } : {}),
              })
            }
          />
        </div>
      </div>
    </dialog>
  );
}

/**
 * What is wrong with the form, in Lao, or null.
 *
 * Checked here only to save the shopkeeper a round trip for something obvious.
 * The server checks all of it again and its answer is the one that decides —
 * the client is the part an attacker controls.
 *
 * Nothing is reported until the current password has been entered, because a
 * form that turns red before the first field is finished is noise.
 */
function check(
  login: string,
  finalLogin: string,
  password: string,
  again: string,
  current: string,
): string | null {
  if (!current) return null;
  if (login && login.length < MIN_LOGIN) {
    return `ຊື່ຜູ້ໃຊ້ຕ້ອງຍາວຢ່າງໜ້ອຍ ${MIN_LOGIN} ຕົວ`;
  }
  if (password && password.length < MIN_PASSWORD) {
    return `ລະຫັດຜ່ານຕ້ອງຍາວຢ່າງໜ້ອຍ ${MIN_PASSWORD} ຕົວ`;
  }
  // Either box filled on its own is a mismatch: typing only into the second one
  // is what happens when somebody taps the wrong field, and silently ignoring
  // it would save a password they did not mean to set — or none at all.
  if ((password || again) && password !== again) {
    return 'ລະຫັດຜ່ານໃໝ່ສອງຊ່ອງບໍ່ຕົງກັນ';
  }
  if (password && password === finalLogin) return 'ລະຫັດຜ່ານຊ້ຳກັບຊື່ຜູ້ໃຊ້';
  if (!login && !password) return 'ໃສ່ຊື່ຜູ້ໃຊ້ໃໝ່ ຫຼື ລະຫັດຜ່ານໃໝ່ຢ່າງໜ້ອຍໜຶ່ງຢ່າງ';
  return null;
}
